/**
 * Bootstrap Tabs Hash URL Handler
 * Handles URL hash updates on tab clicks and activates tabs from hash on load.
 * Supports greedy nav (responsive overflow menu).
 * Zero jQuery dependency.
 */

/**
 * Initialize tabs hash URL handler.
 * Handles hash-based tab activation on load and hash updates on tab clicks.
 *
 * @returns {Function} Cleanup function to remove event listeners
 */
export function initTabsHashHandler() {
	// On page load: activate tab from hash
	handleHashOnLoad();

	// On tab click: update hash without scroll jump
	document.addEventListener('click', handleTabClick, true);

	return () => {
		document.removeEventListener('click', handleTabClick, true);
	};
}

/**
 * Activate tab from URL hash on page load.
 * Also handles smooth scrolling to tab content.
 */
function handleHashOnLoad() {
	const hash = window.location.hash;
	if (!hash) return;

	// Find tab link matching hash
	const tabLink = document.querySelector(`.nav-tabs a[href="${hash}"], .nav-pills a[href="${hash}"]`);

	if (tabLink && window.bootstrap?.Tab) {
		const tab = new bootstrap.Tab(tabLink);
		tab.show();

		// Smooth scroll to tab content (exclude comments/reviews)
		const shouldScroll = hash.indexOf('#comment-') !== 0 && hash.indexOf('#reviews') !== 0;
		const tabContent = document.querySelector(`#gd-single-tabs-content > ${hash}`);

		if (shouldScroll && tabContent) {
			setTimeout(() => {
				const offset = 120;
				const top = tabContent.getBoundingClientRect().top + window.scrollY - offset;
				window.scrollTo({ top, behavior: 'smooth' });
			}, 150);
		}
	}
}

/**
 * Handle tab click to update URL hash.
 * Supports both normal and greedy nav patterns.
 *
 * @param {Event} e - Click event
 */
function handleTabClick(e) {
	const tabLink = e.target.closest('.nav-tabs a, .nav-pills a');
	if (!tabLink) return;

	let hash = '';

	// Check if greedy nav is active
	const greedyNav = tabLink.closest('.greedy');

	if (greedyNav && tabLink.classList.contains('greedy-nav-link')) {
		// Greedy nav: get hash from active link in dropdown
		const activeLink = greedyNav.querySelector('.greedy-links .nav-link.active');
		if (activeLink) {
			hash = activeLink.getAttribute('aria-controls');
			if (hash) hash = '#' + hash;
		} else {
			// No active link, use first link
			const firstLink = greedyNav.querySelector('.greedy-links .nav-link:first-child');
			if (firstLink) {
				hash = firstLink.getAttribute('aria-controls');
				if (hash) hash = '#' + hash;
			}
		}
	} else {
		// Normal nav: use link's hash
		hash = tabLink.hash;
	}

	if (hash) {
		// Save scroll position
		const scrollPos = window.scrollY;
		// Update hash
		window.location.hash = hash;
		// Restore scroll position (prevent jump)
		window.scrollTo(0, scrollPos);
	}
}
